import { ErrorBoundary, createSignal, type ParentProps } from "solid-js"
import { useAuth } from "@/context/auth"
import { useAppInfo } from "@/context/app-info"
import { HttpError } from "@/transport/http"

function AccessRecovery(props: { retry: () => void }) {
  const auth = useAuth()
  const appInfo = useAppInfo()
  const [signingIn, setSigningIn] = createSignal(false)
  const [error, setError] = createSignal("")
  const signIn = async () => {
    if (signingIn()) return
    setSigningIn(true)
    setError("")
    try {
      await auth.signInWithGoogle()
    } catch {
      setError("Could not start sign-in. Check your connection and try again.")
      setSigningIn(false)
    }
  }

  return (
    <main class="flex min-h-dvh items-center justify-center bg-background-base p-6 text-text-base">
      <section class="flex max-w-md flex-col items-center gap-4 text-center" aria-live="polite">
        <h1 class="text-xl font-medium">Connection needs authentication</h1>
        <p>Your access could not be renewed. Retry the connection, or sign in again if your session has expired.</p>
        <div class="flex gap-3">
          <button class="rounded-md border border-border-base px-4 py-2" onClick={props.retry}>Retry connection</button>
          {appInfo.accountAuth && (
            <button class="rounded-md border border-border-base px-4 py-2" disabled={signingIn()} onClick={() => void signIn()}>
              {signingIn() ? "Opening sign-in…" : "Sign in again"}
            </button>
          )}
        </div>
        {!appInfo.accountAuth && <p>For a password-protected server, reopen its address to authenticate in your browser.</p>}
        {error() && <p role="alert">{error()}</p>}
      </section>
    </main>
  )
}

export function AuthenticationBoundary(props: ParentProps) {
  return (
    <ErrorBoundary fallback={(error, reset) => {
      if (!(error instanceof HttpError) || error.status !== 401) throw error
      return <AccessRecovery retry={reset} />
    }}>
      {props.children}
    </ErrorBoundary>
  )
}
